import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TenantVideoDemoSettings } from '../../../shared/services/tenant.service';

@Component({
  selector: 'black-video-demo-section',
  templateUrl: './video-demo-section.component.html',
  standalone: true,
  imports: [CommonModule],
})
export class VideoDemoSectionComponent implements OnChanges {
  private sanitizer = inject(DomSanitizer);

  @Input() videoDemo?: TenantVideoDemoSettings;
  @Input() theme: 'modern' | 'warm' | 'bold' = 'modern';

  youtubeEmbedUrl: SafeResourceUrl | null = null;
  directVideoUrl: string | null = null;

  get title(): string {
    return this.videoDemo?.title || 'Experience A Free Class Demo';
  }

  get subtitle(): string {
    return (
      this.videoDemo?.subtitle ||
      'Watch an excerpt from our live classes and see how complex concepts are simplified with clarity and depth.'
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['videoDemo']) {
      this.parseVideoUrl();
    }
  }

  private parseVideoUrl(): void {
    const rawUrl = this.videoDemo?.videoUrl?.trim();
    if (!rawUrl) {
      this.youtubeEmbedUrl = null;
      this.directVideoUrl = null;
      return;
    }

    // YouTube parser (standard, youtu.be, embed, shorts, live)
    const ytMatch = rawUrl.match(
      /(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|shorts\/|live\/))([\w-]{11})/,
    );
    if (ytMatch) {
      this.youtubeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://www.youtube.com/embed/${ytMatch[1]}?rel=0`,
      );
      this.directVideoUrl = null;
      return;
    }

    // Vimeo parser (standard, player, channels, groups)
    const vimeoMatch = rawUrl.match(
      /(?:vimeo\.com\/(?:video\/|channels\/[\w-]+\/|groups\/[\w-]+\/videos\/)?|player\.vimeo\.com\/video\/)(\d+)/,
    );
    if (vimeoMatch) {
      this.youtubeEmbedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
        `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      );
      this.directVideoUrl = null;
      return;
    }

    // Direct MP4 / WebM (must be http:// or https://)
    if (
      (rawUrl.startsWith('https://') || rawUrl.startsWith('http://')) &&
      (rawUrl.endsWith('.mp4') || rawUrl.endsWith('.webm') || rawUrl.includes('/video/'))
    ) {
      this.directVideoUrl = rawUrl;
      this.youtubeEmbedUrl = null;
      return;
    }

    // Reject untrusted fallback URLs
    this.youtubeEmbedUrl = null;
    this.directVideoUrl = null;
  }
}
